import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SlidersHorizontal } from "lucide-react";
import { ChannelSettlementStatus } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { StatusBadge } from "@/components/status-badge";
import { Amount } from "@/components/amount";
import { AssetLabel } from "@/components/asset-label";
import { MerchantCell } from "@/components/merchant-cell";
import { CHANNEL_SETTLEMENT_STATUS_COLOR } from "@/lib/status-colors";
import { type TableSearch, useTableState } from "@/lib/table-state";
import { cn, formatStatusLabel } from "@/lib/utils";
import { listChannelSettlements } from "@/api/channels";
import { listMerchants } from "@/api/merchants";
import { type ChannelSettlementListItem, type ListChannelSettlementsParams } from "@/api/types";

// Task 14 (feature-031 Phase B): platform-wide channel Settlements list —
// same list-page shape as _app.channels.tsx. `channelId` (a supported
// backend filter) is deliberately not exposed here for the same reason
// _app.channel-payments.tsx omits it — the channel detail page's own
// Settlements sub-table already covers that case directly.
type ChannelSettlementFilters = {
  status?: ChannelSettlementStatus;
  merchantId?: string;
};

const DEFAULT_FILTERS: ChannelSettlementFilters = {};

function validateChannelSettlementsSearch(
  search: Record<string, unknown>,
): TableSearch<ChannelSettlementFilters> {
  return {
    page: typeof search.page === "number" ? search.page : undefined,
    pageSize: typeof search.pageSize === "number" ? search.pageSize : undefined,
    sortBy: typeof search.sortBy === "string" ? search.sortBy : undefined,
    sortOrder: search.sortOrder === "asc" || search.sortOrder === "desc" ? search.sortOrder : undefined,
    status: typeof search.status === "string" ? (search.status as ChannelSettlementStatus) : undefined,
    merchantId: typeof search.merchantId === "string" ? search.merchantId : undefined,
  };
}

export const Route = createFileRoute("/_app/channel-settlements")({
  validateSearch: validateChannelSettlementsSearch,
  component: ChannelSettlementsPage,
});

function formatDate(value: string): string {
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

const SELECT_CLASSNAME = cn(
  "h-9 rounded-md border border-input bg-transparent px-2 text-sm",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
);

function RowLink({ channelSettlementId, children }: { channelSettlementId: string; children: ReactNode }) {
  return (
    <Link
      to="/channel-settlements/$channelSettlementId"
      params={{ channelSettlementId }}
      className="group -m-2 block p-2"
    >
      {children}
    </Link>
  );
}

function MerchantFilterDialog({
  filters,
  setFilter,
  merchants,
}: {
  filters: ChannelSettlementFilters;
  setFilter: <K extends keyof ChannelSettlementFilters>(key: K, value: ChannelSettlementFilters[K]) => void;
  merchants: Array<{ id: string; businessName: string }>;
}) {
  const [open, setOpen] = useState(false);
  const activeCount = filters.merchantId !== undefined ? 1 : 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="gap-1.5">
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Filters
          {activeCount > 0 && (
            <span className="ml-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
              {activeCount}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Filter settlements</DialogTitle>
          <DialogDescription>Narrow the list by merchant.</DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="filter-merchant">Merchant</Label>
          <select
            id="filter-merchant"
            className={cn(SELECT_CLASSNAME, "w-full")}
            value={filters.merchantId ?? "ALL"}
            onChange={(e) => setFilter("merchantId", e.target.value === "ALL" ? undefined : e.target.value)}
          >
            <option value="ALL">All merchants</option>
            {merchants.map((m) => (
              <option key={m.id} value={m.id}>
                {m.businessName}
              </option>
            ))}
          </select>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button type="button" variant="ghost" size="sm" disabled={activeCount === 0} onClick={() => setFilter("merchantId", undefined)}>
            Clear this filter
          </Button>
          <Button type="button" size="sm" onClick={() => setOpen(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChannelSettlementsPage() {
  const tableState = useTableState<ChannelSettlementFilters>({
    defaultFilters: DEFAULT_FILTERS,
    defaultSortBy: "createdAt",
    defaultSortOrder: "desc",
  });

  const params: ListChannelSettlementsParams = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: (tableState.sortBy ?? undefined) as ListChannelSettlementsParams["sortBy"],
    sortOrder: tableState.sortOrder,
    ...tableState.filters,
  };

  const { data, isPending, isError } = useQuery({
    queryKey: ["channel-settlements", params],
    queryFn: () => listChannelSettlements(params),
  });

  const { data: merchants } = useQuery({
    queryKey: ["merchants"],
    queryFn: listMerchants,
  });
  const sortedMerchants = [...(merchants ?? [])].sort((a, b) => a.businessName.localeCompare(b.businessName));

  const anyFilterActive = Object.values(tableState.filters).some((v) => v !== undefined);

  const columns: DataTableColumn<ChannelSettlementListItem>[] = [
    {
      key: "publicId",
      header: "Settlement ID",
      cell: (s) => (
        <RowLink channelSettlementId={s.id}>
          <span className="font-mono group-hover:underline">{s.publicId}</span>
        </RowLink>
      ),
    },
    {
      key: "merchant",
      header: "Merchant",
      cell: (s) => (
        <MerchantCell merchantId={s.merchant.id} businessName={s.merchant.businessName} publicId={s.merchant.publicId} />
      ),
    },
    {
      key: "channel",
      header: "Channel",
      cell: (s) => (
        <Link to="/channels/$channelId" params={{ channelId: s.channel.id }} className="group -m-2 block p-2">
          <span className="block group-hover:underline">{s.channel.label}</span>
          <span className="block text-xs text-muted-foreground">{s.channel.publicId}</span>
        </Link>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (s) => (
        <RowLink channelSettlementId={s.id}>
          <StatusBadge label={formatStatusLabel(s.status)} color={CHANNEL_SETTLEMENT_STATUS_COLOR[s.status]} />
        </RowLink>
      ),
    },
    {
      key: "asset",
      header: "Asset",
      cell: (s) => (
        <RowLink channelSettlementId={s.id}>
          <AssetLabel symbol={s.asset.symbol} network={s.asset.network} />
        </RowLink>
      ),
    },
    {
      key: "grossAmount",
      header: "Total Amount",
      sortable: true,
      cell: (s) => (
        <RowLink channelSettlementId={s.id}>
          <Amount native={s.grossAmount} symbol={s.asset.symbol} />
        </RowLink>
      ),
    },
    {
      key: "paymentCount",
      header: "Payments",
      cell: (s) => <RowLink channelSettlementId={s.id}>{s.paymentCount}</RowLink>,
    },
    {
      key: "declines",
      header: "Declines (sweep/gas)",
      cell: (s) => (
        <RowLink channelSettlementId={s.id}>
          {s.sweepDeclineCount} / {s.gasFundDeclineCount}
        </RowLink>
      ),
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      cell: (s) => <RowLink channelSettlementId={s.id}>{formatDate(s.createdAt)}</RowLink>,
    },
  ];

  return (
    <div className="space-y-6">
      <DataTable
        columns={columns}
        items={data?.data ?? []}
        total={data?.total ?? 0}
        page={tableState.page}
        pageSize={tableState.pageSize}
        sortBy={tableState.sortBy}
        sortOrder={tableState.sortOrder}
        loading={isPending}
        error={isError ? "Failed to load settlements." : null}
        onPageChange={(page) => tableState.setPage(page)}
        onPageSizeChange={(pageSize) => tableState.setPageSize(pageSize)}
        onSortChange={(columnKey) => tableState.setSort(columnKey)}
        filterBar={
          <>
            <select
              aria-label="Status"
              className={SELECT_CLASSNAME}
              value={tableState.filters.status ?? "ALL"}
              onChange={(e) =>
                tableState.setFilter(
                  "status",
                  e.target.value === "ALL" ? undefined : (e.target.value as ChannelSettlementStatus),
                )
              }
            >
              <option value="ALL">All statuses</option>
              {Object.values(ChannelSettlementStatus).map((s) => (
                <option key={s} value={s}>
                  {formatStatusLabel(s)}
                </option>
              ))}
            </select>

            <MerchantFilterDialog filters={tableState.filters} setFilter={tableState.setFilter} merchants={sortedMerchants} />

            {anyFilterActive && (
              <Button type="button" variant="ghost" size="sm" onClick={() => tableState.reset()}>
                Clear filters
              </Button>
            )}
          </>
        }
        getRowKey={(s) => s.id}
        emptyMessage="No settlements match your filters."
      />
    </div>
  );
}
